import React from 'react';
import {
  PageHead, Section, Eyebrow, PageTitle, Lede, SectionHead, Card, CardTitle, CardBody, Grid, Saying, Button,
} from '../components/ui';

/**
 * Why it matters. The argument, once: the Cognitive Rust Belt, the Mastery
 * Loop, the 5Cs, the AI Fluency Ladder. Merged from the first site's WHY and HOW.
 */

const RESEARCH = [
  { value: '83%', label: 'of AI-assisted workers could not recall what they had just written', source: 'MIT Media Lab, 2025' },
  { value: '80%', label: 'of AI projects fail, twice the rate of other technology projects', source: 'RAND Corporation, 2024' },
  { value: '60%', label: 'of workers using AI daily have received no formal training', source: 'Fortune, 2026' },
  { value: '3x',  label: 'greater return when humans stay in the Think, Do, Learn, Adapt loop', source: 'McKinsey, 2025' },
];

const LOOP = [
  ['Think', 'Frame the problem. Form a hypothesis. Apply judgment.'],
  ['Do', 'Act on the thinking. Commit to a direction. Make something real.'],
  ['Learn', 'Process the result. Build pattern recognition from experience.'],
  ['Adapt', 'Update the mental model. Get sharper for the next cycle.'],
];

const FIVE_CS = [
  ['Conceptual Clarity', 'Know the terrain', 'What AI is, what it can and cannot do, and why it matters in your context. Understanding, without awe and without fear.'],
  ['Creative Exploration', 'Venture further', 'AI as a passport into domains beyond your expertise. Somewhere you could not have reached alone, rather than deeper into familiar ground.'],
  ['Critical Evaluation', 'Question the answer', 'The discipline to challenge what AI gives you, find the assumption buried in the answer, and keep your judgment as the final authority.'],
  ['Constructive Collaboration', 'Think together', 'AI as a working thought partner: structuring problems, guiding the dialogue, the human firmly in the driver’s seat.'],
  ['Cognitive Agility', 'Stay in the loop', 'The connector that makes the others sustainable. Keep evolving as AI evolves, updating mental models without losing core judgment.'],
];



const ArgumentPage = () => (
  <>
    <PageHead
      title="Why it matters"
      description="Cognitive atrophy, AI Fluency, and the Compass. Why the real danger with AI is what it lets you not do, the five human capabilities that gain value as AI gains capability, and how to find out where you stand."
      path="/why-it-matters"
    />

    <Section tone="page" reveal={false}>
      <Eyebrow>Why it matters</Eyebrow>
      <PageTitle>The real danger with AI is not what it does. It is what it lets you not do.</PageTitle>
      <Lede>Most organizations treat AI like a software rollout: deploy the tools, train for compliance, measure adoption. That is the wrong frame, and it is why so many AI initiatives produce no measurable value. You cannot reimagine a business until the people inside it can think with AI themselves.</Lede>
    </Section>

    <Section tone="card" id="cognitive-atrophy" className="scroll-mt-20">
      <Eyebrow>Cognitive atrophy</Eyebrow>
      <SectionHead>This is how humans build mastery. AI makes it easy to stop.</SectionHead>
      <div className="prose-rbl mt-5">
        <p>Nobody learns to ride a bike by reading about it. You Think about what to do, you Do it, you Learn from what happened, and you Adapt your next attempt. That loop, run many times, is how every skill worth having gets built, from a surgeon’s hands to a leader’s judgment. We call it the Mastery Loop.</p>
      </div>
      <Grid cols={4} className="mt-8">
        {LOOP.map(([t, d], i) => (
          <Card key={t} className={i === 3 ? 'border-emerald-500 bg-emerald-50 dark:bg-navy-850' : 'bg-navy-50 dark:bg-navy-900'}>
            <CardTitle>{t}</CardTitle>
            <CardBody>{d}</CardBody>
          </Card>
        ))}
      </Grid>

      <h3 className="font-display font-bold text-subhead text-navy-800 dark:text-white mt-14">AI breaks the loop.</h3>
      <div className="prose-rbl mt-4">
        <p>Every tool before AI took over the Do. Humans still had to Think first: frame the problem, form the judgment, decide what to build. The loop stayed intact and mastery kept compounding.</p>
        <p>AI is different. It produces outputs that look and feel like the result of thinking. Polished, confident, fast. So people stop doing the thinking themselves, not because they are told to, but because the friction disappears. Why wrestle with a hard problem when a clean answer arrives in seconds?</p>
        <p>The loop does not break loudly. It atrophies. Quietly, efficiently, until the moment that requires human judgment arrives and the capacity for it is gone. That is the Cognitive Rust Belt: the abdication of human agency and the erosion of creativity, judgment, and what makes us unique. Cognitive atrophy, not job loss, is the risk to name.</p>
      </div>
      <Saying tag="Critical Evaluation" className="mt-12">A thousand small abdications make one great surrender.</Saying>
      <Grid cols={4} className="mt-10">
        {RESEARCH.map((r) => (
          <div key={r.source} className="rounded-lg border border-navy-200 dark:border-navy-700 bg-navy-50 dark:bg-navy-900 p-5">
            <div className="font-sans font-semibold text-stat-lg text-navy-800 dark:text-white">{r.value}</div>
            <div className="font-sans text-interface mt-2 text-navy-600 dark:text-[#C9D2E0]">{r.label}</div>
            <div className="font-mono text-meta mt-2 text-navy-500 dark:text-navy-400">{r.source}</div>
          </div>
        ))}
      </Grid>
    </Section>

    <Section tone="card" id="ai-fluency" className="scroll-mt-20">
      <Eyebrow>AI Fluency</Eyebrow>
      <SectionHead>AI Fluency is not AI Literacy. The 5Cs are the difference.</SectionHead>
      <p className="prose-rbl mt-5">AI Literacy is basic awareness and tool familiarity, the generic market term. AI Fluency is a practiced capability: the ability to engage AI as a thought partner, critic, collaborator, and amplifier while keeping human judgment as the final authority. It compounds through use and decays through passivity. These are the five practices that keep the loop alive.</p>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[14px]">
        {FIVE_CS.map(([name, practice, body], i) => (
          <Card key={name} accent as="article">
            <div className="font-mono text-meta text-navy-500 dark:text-navy-400 mb-1">0{i + 1}</div>
            <CardTitle>{name}</CardTitle>
            <div className="font-sans text-label uppercase font-semibold text-emerald-600 dark:text-emerald-300 mt-2">{practice}</div>
            <CardBody>{body}</CardBody>
          </Card>
        ))}
      </div>
      <Saying tag="Constructive Collaboration" className="mt-12">An answer from AI is the end of thinking. A better question is the start of it.</Saying>
    </Section>

    <Section tone="page" id="compass" className="scroll-mt-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <Eyebrow>The Compass</Eyebrow>
          <SectionHead>Find out where you stand.</SectionHead>
          <p className="prose-rbl mt-4">The CoThink Compass puts the argument to work on you. Ten questions, two per capability, place you on the AI Fluency Ladder and send a report with next steps to your inbox. Free, no account, ten minutes.</p>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Button to="/compass">Take the Compass</Button>
        </div>
      </div>
    </Section>
  </>
);

export default ArgumentPage;
